// Word file types — SPEC_WORKFLOW §12: anywhere a Word file can be picked,
// .doc .docx .dot .dotx .dotm are accepted. Single source for every picker.

export const WORD_FILE_EXTENSIONS: readonly string[] = ['.doc', '.docx', '.dot', '.dotx', '.dotm'];

/** For <input accept>. */
export const WORD_FILE_ACCEPT = '.doc,.docx,.dot,.dotx,.dotm';

/** Electron dialog filters. */
export const WORD_FILE_FILTERS: { name: string; extensions: string[] }[] = [
  { name: 'Word documents', extensions: ['doc', 'docx', 'dot', 'dotx', 'dotm'] },
];

export const LEGACY_WORD_NOT_PROCESSED_MESSAGE =
  'Word 97–2003 file — open in Word, Save As .docx, run again.';

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i).toLowerCase();
}

export function isWordFile(name: string): boolean {
  return WORD_FILE_EXTENSIONS.includes(extOf(name));
}

/** .doc / .dot — binary formats that need conversion to .docx before processing. */
export function isLegacyWordFile(name: string): boolean {
  const e = extOf(name);
  return e === '.doc' || e === '.dot';
}

export function isWordTemplateFile(name: string): boolean {
  const e = extOf(name);
  return e === '.dot' || e === '.dotx' || e === '.dotm';
}

export type WordConvertResult =
  | { ok: true; docxPath: string }
  | { ok: false; reason: 'word-missing' | 'locked' | 'failed'; message: string };

/** Shown for a file Word has open (D3, 2026-10-13). */
export const WORD_LOCKED_NOT_PROCESSED_MESSAGE =
  'File locked open in Word — close it (or delete the stale ~$ file beside it) and run again.';

/** Sync C 7.3 — the lock row names who holds the file when Windows exposes
    the owner. Falls back to the plain message. Pure. */
export function wordLockedMessage(holder?: string | null): string {
  const name = (holder || '').trim();
  if (!name) return WORD_LOCKED_NOT_PROCESSED_MESSAGE;
  return `File locked open in Word by ${name} — close it (or delete the stale ~$ file beside it) and run again.`;
}

/** The user name Word writes at the start of a `~$name.docx` owner file:
    byte 0 = name length (max 52), then the name in ANSI. Null when the bytes
    do not look like one. Pure. */
export function parseWordOwnerName(bytes: Uint8Array): string | null {
  if (!bytes || bytes.length < 2) return null;
  const len = bytes[0];
  if (len < 1 || len > 52 || bytes.length < 1 + len) return null;
  let out = '';
  for (let i = 1; i <= len; i++) {
    const c = bytes[i];
    if (c < 32) return null;
    out += String.fromCharCode(c);
  }
  const name = out.trim();
  return name || null;
}

/** One owner-file name to look for. `exact` = "~$" + the whole name; the
    fuzzy ones drop the first one or two characters (Word keeps the owner
    name short for longer names) and so can belong to a DIFFERENT file in
    the same folder — those only count when recent (OWNER_FILE_FUZZY_MAX_AGE_MS). */
export interface WordOwnerFileCandidate {
  name: string;
  exact: boolean;
}

/** A fuzzy owner-file match older than this is treated as unrelated (a
    stale leftover or another file's owner file), never as a lock. */
export const OWNER_FILE_FUZZY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Word's owner ("lock") file names for `fileName`, as Word writes them
    beside the open document. Pure. */
export function wordOwnerFileCandidates(fileName: string): WordOwnerFileCandidate[] {
  const out: WordOwnerFileCandidate[] = [{ name: `~$${fileName}`, exact: true }];
  const seen = new Set(out.map((c) => c.name));
  for (const drop of [1, 2]) {
    if (fileName.length <= drop) break;
    const name = `~$${fileName.slice(drop)}`;
    if (!seen.has(name)) {
      seen.add(name);
      out.push({ name, exact: false });
    }
  }
  return out;
}

/** Does an existing owner file mean the document is locked? The exact name
    always does; a fuzzy name only when it was written within the last 24h.
    Pure. */
export function ownerFileMeansLocked(
  candidate: WordOwnerFileCandidate,
  ownerMtimeMs: number,
  nowMs: number
): boolean {
  if (candidate.exact) return true;
  return nowMs - ownerMtimeMs <= OWNER_FILE_FUZZY_MAX_AGE_MS;
}

/** One input's pre-run lock probe. `locked` = skip it with
    WORD_LOCKED_NOT_PROCESSED_MESSAGE. `unchecked` = the probe could not run
    (path not granted, file missing) — the run proceeds and the format step
    reports its own error for that file. */
export interface WordLockProbe {
  path: string;
  status: 'free' | 'locked' | 'unchecked';
  /** Why — 'owner-file' | 'busy' | the probe's own error text. */
  reason?: string;
  /** Who holds it, when the owner file names them (Sync C 7.3). */
  holder?: string;
}
